/**
 * Permits for a single job (companion §2.8 "permit list per job").
 *
 * Wiring (mobile inv. 7): useQuery(listPermitsByJob). Loading / empty / error render.
 * Tap a row → permit detail. "New filing" carries the jobId into the new-filing screen.
 * NEVER imports @smartbox/*.
 */

import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useQuery } from '@tanstack/react-query';
import { listPermitsByJob, type PermitApplicationRow } from '../../../src/api/permits';
import { Screen, Text, Badge, Card, Button, ListRow } from '../../../src/components/ui';
import type { StatusKey } from '../../../src/theme';
import { theme } from '../../../src/theme';
import { extractErrorMessage, humanize } from '../../../src/lib/format';

function permitBadge(status: string): { key: StatusKey; label: string } {
  switch (status) {
    case 'issued': case 'closed': return { key: 'paid', label: humanize(status) };
    case 'submitted': return { key: 'sent', label: 'Submitted' };
    case 'in_review': case 'under_review': case 'sufficiency_review': case 'fees_due': case 'fees_paid':
      return { key: 'inProgress', label: humanize(status) };
    case 'rejected': case 'withdrawn': case 'corrections_required': return { key: 'overdue', label: humanize(status) };
    default: return { key: 'draft', label: humanize(status) || 'Draft' };
  }
}

export default function PermitsByJobScreen() {
  const router = useRouter();
  const { jobId } = useLocalSearchParams<{ jobId: string }>();

  const query = useQuery({
    queryKey: ['permits-by-job', jobId],
    queryFn: () => listPermitsByJob(jobId!),
    enabled: !!jobId,
    staleTime: 30_000,
  });

  if (!jobId) {
    return <Screen><View style={styles.centered} testID="permits-by-job-screen"><Text variant="body" color="danger">No job ID provided.</Text></View></Screen>;
  }
  if (query.isLoading) {
    return (
      <Screen>
        <View style={styles.centered} testID="permits-by-job-screen">
          <ActivityIndicator size="large" color={theme.color.action.primary} accessibilityLabel="Loading permits" />
          <Text variant="body" color="secondary">Loading…</Text>
        </View>
      </Screen>
    );
  }
  if (query.isError) {
    return (
      <Screen>
        <View style={styles.centered} testID="permits-by-job-screen">
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">{extractErrorMessage(query.error, 'Failed to load permits.')}</Text>
            <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => query.refetch()} />
          </Card>
        </View>
      </Screen>
    );
  }

  const apps = query.data ?? [];

  return (
    <Screen scroll>
      <View testID="permits-by-job-screen" style={styles.section}>
        <Text variant="title">Job permits</Text>
        <Button
          title="New filing for this job"
          testID="permits-by-job-new"
          onPress={() => router.push({ pathname: '/permits/new', params: { jobId } })}
          accessibilityLabel="Start a new permit filing for this job"
        />
        {apps.length === 0 ? (
          <Card><Text variant="body" color="secondary">No permits for this job yet.</Text></Card>
        ) : (
          <View style={styles.list} testID="permits-by-job-list">
            {apps.map((app: PermitApplicationRow, i: number) => {
              const b = permitBadge(app.status);
              return (
                <View key={app.id} testID={`permits-by-job-item-${i}`}>
                  <ListRow
                    title={app.scope_of_work || app.id.slice(0, 8).toUpperCase()}
                    subtitle={humanize(app.status)}
                    right={<Badge status={b.key} label={b.label} />}
                    onPress={() => router.push(`/(app)/permits/${app.id}`)}
                  />
                </View>
              );
            })}
          </View>
        )}
      </View>
    </Screen>
  );
}

const styles = StyleSheet.create({
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center', gap: theme.spacing.md, paddingHorizontal: theme.spacing['2xl'] },
  section: { gap: theme.spacing.sm },
  list: { gap: theme.spacing.sm },
});
